import { createAction, createActionGroup, emptyProps, props } from "@ngrx/store";
import { Resource, Procedure, ProcedureInstance, ProcedureInstanceStatus, ProcedureStepRedline, ProcedureVersion, ProcedureVersionStep, SaveStatus, ScheduledProcedure, FactoryAsset, MappingAssetToResource, MappingAssetToProcedure, FreightCarrier, FactoryAssetType, Customer, Vendor, PurchaseRequestList, PurchaseRequest, Contact, SoftwareRequest, AssetHistoryEvent, PurchaseOrder, CapitalizationProject, UploadedFile } from "@prism/prism-shared";

export const ResetState = createAction('Common ResetState');

export const LoadResources = createActionGroup({
    source: 'Common LoadResources',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ resources: Resource[] }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const SetResource = createActionGroup({
    source: 'Common SetResource',
    events: {
        'Initial': props<{ resource: Resource }>(),
        'Success': props<{ resource: Resource }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadResourceById = createActionGroup({
    source: 'Common LoadResourceById',
    events: {
        'Initial': props<{ resourceId: string }>(),
        'Success': props<{ resource: Resource }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadResourcesByIds = createActionGroup({
    source: 'Common LoadResourcesByIds',
    events: {
        'Initial': props<{ resourceIds: string[]; }>(),
        'Success': props<{ resources: Resource[]; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadActiveResources = createActionGroup({
    source: 'Common LoadActiveResources',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ resources: Resource[] }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadProcedures = createActionGroup({
    source: 'Common LoadProcedures',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ procedures: Procedure[]; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadProceduresAndHighestReleasedVersion = createActionGroup({
    source: 'Common LoadProceduresAndHighestReleasedVersion',
    events: {
        'Initial': emptyProps(),
        'Success': props<{
            procedures: Procedure[];
            procedureVersions: ProcedureVersion[];
        }>(),
        'Failure': props<{ error: any }>(),
    }
});
export const LoadReleasedPlasticsProcedureVersions = createActionGroup({
    source: 'Common LoadReleasedPlasticsProcedureVersions',
    events: {
        'Initial': emptyProps(),
        'Success': props<{
            procedureVersions: ProcedureVersion[];
        }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadCompletedProcedureInstancesWithLimit = createActionGroup({
    source: 'Common LoadCompletedProcedureInstancesWithLimit',
    events: {
        'Initial': props<{ limit: number }>(),
        'Success': props<{ instances: ProcedureInstance[]; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadProcedureInstancesByStatuses = createActionGroup({
    source: 'Common LoadProcedureInstancesByStatuses',
    events: {
        'Initial': props<{ statuses: ProcedureInstanceStatus[]; }>(),
        'Success': props<{ instances: ProcedureInstance[]; }>(),
        'Set': props<{ instances: ProcedureInstance[] }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadProcedureVersionsByIds = createActionGroup({
    source: 'Common LoadProcedureVersionsByIds',
    events: {
        'Initial': props<{ procedureVersionIds: string[]; }>(),
        'Success': props<{ procedureVersions: ProcedureVersion[]; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const ViewProcedureLoadProcedureData = createActionGroup({
    source: 'Common ViewProcedureLoadProcedureData',
    events: {
        'Initial': props<{
            procedureId: string;
            procedureVersionId: string;
        }>(),
        'Success': props<{
            selectedProcedure: Procedure;
            selectedProcedureVersions: ProcedureVersion[];
            selectedProcedureVersionId: string;
            scheduledProcedures: ScheduledProcedure[];
            factoryAssets: FactoryAsset[];
        }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const SetProcedureVersion = createActionGroup({
    source: 'Common SetProcedureVersion',
    events: {
        'Initial': props<{ procedureVersion: ProcedureVersion; }>(),
        'Success': props<{ procedureVersion: ProcedureVersion; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const SetProcedure = createActionGroup({
    source: 'Common SetProcedure',
    events: {
        'Initial': props<{ procedure: Procedure }>(),
        'Success': props<{ procedure: Procedure; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const ViewProcedureUpdate = createActionGroup({
    source: 'Common ViewProcedureUpdate',
    events: {
        'UpdateSelectedProcdureVersion': props<{ procedureVersion: ProcedureVersion }>(),
        'UpdateSelectedProcedure': props<{ procedure: Procedure }>(),
        'SetSelectedProcedureVersionid': props<{ procedureVersionId: string }>(),
    }
});

export const EditProcedureLoadProcedureData = createActionGroup({
    source: 'Common EditProcedureLoadProcedureData',
    events: {
        'Initial': props<{
            procedureId: string;
            procedureVersionId: string;
        }>(),
        'Success': props<{
            procedure: Procedure;
            procedureVersion: ProcedureVersion;
            redlines: ProcedureStepRedline[];
        }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const EditProcedure = createActionGroup({
    source: 'Common EditProcedure',
    events: {
        'UpdateName': props<{ name: string; }>(),
        'UpdateDescription': props<{ description: string; }>(),
        'CreateStep': props<{ step: ProcedureVersionStep; }>(),
        'DeleteStep': props<{ stepId: string; }>(),
        'UpdateStep': props<{ step: ProcedureVersionStep; }>(),
        'UpdateSteps': props<{ steps: ProcedureVersionStep[]; }>(),
        'UpdatePrefaceMarkdown': props<{ prefaceMarkdown: string; }>(),
        'UpdateSaveStatus': props<{ saveStatus: SaveStatus; }>(),
        'SetSelectedRedlinePanelStepId': props<{ stepId: string | null; }>(),
        'ToggleExpandRedline': props<{ redlineId: string; }>(),
    }
});

export const EditProcedureSetProcedureVersion = createActionGroup({
    source: 'Common EditProcedureSetProcedureVersion',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ procedureVersion: ProcedureVersion }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const DeleteProcedureVersion = createActionGroup({
    source: 'Common DeleteProcedureVersion',
    events: {
        'Initial': props<{ versionId: string }>(),
        'Success': props<{ versionId: string }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const EditProcedureReleaseProcedureVersion = createActionGroup({
    source: 'Common EditProcedureReleaseProcedureVersion',
    events: {
        'Initial': props<{ releaseComment: string; }>(),
        'Success': props<{ procedureVersion: ProcedureVersion }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const EditProcedureSetProcedureStepRedline = createActionGroup({
    source: 'Common EditProcedureSetProcedureStepRedline',
    events: {
        'Initial': props<{ redline: ProcedureStepRedline }>(),
        'Success': props<{ redline: ProcedureStepRedline }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const SetProcedureStepRedline = createActionGroup({
    source: 'Common SetProcedureStepRedline',
    events: {
        'Initial': props<{ redline: ProcedureStepRedline }>(),
        'Success': props<{ redline: ProcedureStepRedline }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const SetScheduledProcedure = createActionGroup({
    source: 'Common SetScheduledProcedure',
    events: {
        'Initial': props<{ scheduledProcedure: ScheduledProcedure }>(),
        'Success': props<{ scheduledProcedure: ScheduledProcedure }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const DeleteScheduledProcedure = createActionGroup({
    source: 'Common DeleteScheduledProcedure',
    events: {
        'Initial': props<{ scheduledProcedure: ScheduledProcedure }>(),
        'Success': props<{ scheduledProcedure: ScheduledProcedure }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadScheduledProcedures = createActionGroup({
    source: 'Common LoadScheduledProcedures',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ scheduledProcedures: ScheduledProcedure[]; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadScheduledProceduresByProcedureId = createActionGroup({
    source: 'Common LoadScheduledProceduresByProcedureId',
    events: {
        'Initial': props<{ procedureId: string; }>(),
        'Success': props<{ scheduledProcedures: ScheduledProcedure[]; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadScheduledProcedureDataByAssetId = createActionGroup({
    source: 'Common LoadScheduledProceduresByAssetId',
    events: {
        'Initial': props<{ assetId: string; }>(),
        'Success': props<{
            scheduledProcedures: ScheduledProcedure[];
            procedures: Procedure[];
            procedureVersions: ProcedureVersion[];
        }>(),
        'Failure': props<{ error: any }>(),
    }
});
export const LoadAssetHistoryEventsByAssetId = createActionGroup({
    source: 'Common LoadAssetHistoryEventsByAssetId',
    events: {
        'Initial': props<{ assetId: string }>(),
        'Success': props<{ assetHistoryEvents: AssetHistoryEvent[] }>(),
        'Failure': props<{ error: any }>(),
    }
});
export const SetAssetHistoryEvent = createActionGroup({
    source: 'Common SetAssetHistoryEvent',
    events: {
        'Initial': props<{ event: AssetHistoryEvent }>(),
        'Success': props<{ event: AssetHistoryEvent }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const SetFactoryAsset = createActionGroup({
    source: 'Common SetFactoryAsset',
    events: {
        'Initial': props<{ factoryAsset: FactoryAsset }>(),
        'Success': props<{ factoryAsset: FactoryAsset }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadFactoryAssets = createActionGroup({
    source: 'Common LoadFactoryAssets',
    events: {
        'Initial': emptyProps,
        'Success': props<{ factoryAssets: FactoryAsset[] }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadFactoryAssetsByType = createActionGroup({
    source: 'Common LoadFactoryAssetsByType',
    events: {
        'Initial': props<{ assetType: FactoryAssetType }>(),
        'Success': props<{ factoryAssets: FactoryAsset[] }>(),
        'Failure': props<{ error: any }>(),
    }
});


export const LoadFactoryAssetById = createActionGroup({
    source: 'Common LoadFactoryAssetById',
    events: {
        'Initial': props<{ assetId: string }>(),
        'Success': props<{ factoryAsset: FactoryAsset }>(),
        'Failure': props<{ error: any }>()
    }
})

export const LoadResourceByFactoryAssetId = createActionGroup({
    source: 'Common LoadResourcesByFactoryAssetId',
    events: {
        'Initial': props<{ assetId: string }>(),
        'Success': props<{ resources: Resource[] }>(),
        'Failure': props<{ error: any }>()
    }
});

export const LoadFactoryAssetsByResourceId = createActionGroup({
    source: 'Common LoadFactoryAssetsByResourceId',
    events: {
        'Initial': props<{ resourceId: string }>(),
        'Success': props<{ factoryAssets: FactoryAsset[] }>(),
        'Failure': props<{ error: any }>(),
    }
})


export const LoadMappingAssetToResources = createActionGroup({
    source: 'Common LoadMappingAssetToResources',
    events: {
        'Initial': emptyProps,
        'Success': props<{ mappingAssetToResources: MappingAssetToResource[] }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadMappingAssetToResourcesByAssetId = createActionGroup({
    source: 'Common LoadMappingAssetToResourcesByAssetId',
    events: {
        'Initial': props<{ assetId: string }>(),
        'Success': props<{ mappingAssetToResources: MappingAssetToResource[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const SetMappingAssetToResource = createActionGroup({
    source: 'Common SetMappingAssetToResource',
    events: {
        'Initial': props<{ mappingAssetToResource: MappingAssetToResource }>(),
        'Success': props<{ mappingAssetToResource: MappingAssetToResource }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const DeleteMappingAssetToResource = createActionGroup({
    source: 'Common DeleteMappingAssetToResource',
    events: {
        'Initial': props<{ assetId: string, resourceId: string }>(),
        'Success': props<{ mappingAssetToResource: MappingAssetToResource }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadProceduresAndReleasedVersionsByAssetId = createActionGroup({
    source: 'Common LoadReleasedProcedureVersionsByAssetId',
    events: {
        'Initial': props<{ assetId: string }>(),
        'Success': props<{ procedureVersions: ProcedureVersion[], procedures: Procedure[] }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadMappingAssetToProceduresByAssetId = createActionGroup({
    source: 'Common LoadMappingAssetToProceduresByAssetId',
    events: {
        'Initial': props<{ assetId: string }>(),
        'Success': props<{ mappingAssetToProcedures: MappingAssetToProcedure[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const SetMappingAssetToProcedure = createActionGroup({
    source: 'Common SetMappingAssetToProcedure',
    events: {
        'Initial': props<{ mappingAssetToProcedure: MappingAssetToProcedure }>(),
        'Success': props<{ mappingAssetToProcedure: MappingAssetToProcedure }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const DeleteMappingAssetToProcedure = createActionGroup({
    source: 'Common DeleteMappingAssetToProcedure',
    events: {
        'Initial': props<{ assetId: string, procedureId: string }>(),
        'Success': props<{ mappingAssetToProcedure: MappingAssetToProcedure }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadBrowseProceduresData = createActionGroup({
    source: 'Common LoadBrowseProceduresData',
    events: {
        'Initial': emptyProps(),
        'Success': props<{
            instances: ProcedureInstance[];
            procedures: Procedure[];
            versions: ProcedureVersion[];
        }>(),
        'Failure': props<{ error: any }>(),
    },
})

export const SetFreightCarrier = createActionGroup({
    source: 'Common SetFreightCarrier',
    events: {
        'Initial': props<{ freightCarrier: FreightCarrier }>(),
        'Success': props<{ freightCarrier: FreightCarrier }>(),
        'Failure': props<{ error: any }>()
    }
})

export const LoadFreightCarriers = createActionGroup({
    source: 'Common LoadFreightCarriers',
    events: {
        'Initial': emptyProps,
        'Success': props<{ freightCarriers: FreightCarrier[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const SetCapitalizationProject = createActionGroup({
    source: 'Common SetCapitalizationProject',
    events: {
        'Initial': props<{ project: CapitalizationProject }>(),
        'Success': props<{ project: CapitalizationProject }>(),
        'Failure': props<{ error: any }>(),
    },
})

export const LoadCapitalizationProjects = createActionGroup({
    source: 'Common LoadCapitalizationProjects',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ projects: CapitalizationProject[] }>(),
        'Failure': props<{ error: any }>(),
    },
})

export const LoadCustomers = createActionGroup({
    source: 'Common LoadCustomers',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ customers: Customer[]; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadCustomerById = createActionGroup({
    source: 'Common LoadCustomerById',
    events: {
        'Initial': props<{ customerId: string; }>(),
        'Success': props<{ customer: Customer; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadCustomersByIds = createActionGroup({
    source: 'Common LoadCustomersByIds',
    events: {
        'Initial': props<{ customerIds: string[]; }>(),
        'Success': props<{ customers: Customer[]; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const SetCustomer = createActionGroup({
    source: 'Common SetCustomer',
    events: {
        'Initial': props<{ customer: Customer; }>(),
        'Success': props<{ customer: Customer; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadPlasticsCustomers = createActionGroup({
    source: 'Common LoadPlasticsCustomers',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ customers: Customer[] }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadMetalsCustomers = createActionGroup({
    source: 'Common LoadMetalsCustomers',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ customers: Customer[] }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadC3Customers = createActionGroup({
    source: 'Common LoadC3Customers',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ customers: Customer[] }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadVendors = createActionGroup({
    source: 'Common LoadVendors',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ vendors: Vendor[]; }>(),
        'Failure': props<{ error: any }>(),
    }
});
export const LoadPlasticsVendors = createActionGroup({
    source: 'Common LoadPlasticsVendors',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ vendors: Vendor[]; }>(),
        'Failure': props<{ error: any }>(),
    }
});
export const LoadMetalsVendors = createActionGroup({
    source: 'Common LoadMetalsVendors',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ vendors: Vendor[]; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadC3Vendors = createActionGroup({
    source: 'Common LoadC3Vendors',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ vendors: Vendor[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const LoadAutomationVendors = createActionGroup({
    source: 'Common LoadAutomationVendors',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ vendors: Vendor[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const LoadVendorById = createActionGroup({
    source: 'Common LoadVendorById',
    events: {
        'Initial': props<{ vendorId: string }>(),
        'Success': props<{ vendor: Vendor; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const LoadVendorsByIds = createActionGroup({
    source: 'Common LoadVendorsByIds',
    events: {
        'Initial': props<{ vendorIds: string[] }>(),
        'Success': props<{ vendors: Vendor[] }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const SetVendor = createActionGroup({
    source: 'Common SetVendor',
    events: {
        'Initial': props<{ vendor: Vendor; }>(),
        'Success': props<{ vendor: Vendor; }>(),
        'Failure': props<{ error: any }>(),
    }
});

export const SetPurchaseRequestList = createActionGroup({
    source: 'Common SetPurchaseRequestList',
    events: {
        'Initial': props<{ purchaseRequestList: PurchaseRequestList }>(),
        'Success': props<{ purchaseRequestList: PurchaseRequestList }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadPurchaseRequestLists = createActionGroup({
    source: 'Common LoadPurchaseRequestLists',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ purchaseRequestLists: PurchaseRequestList[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const LoadPurchaseRequestListById = createActionGroup({
    source: 'Common LoadPurchaseRequestListById',
    events: {
        'Initial': props<{ purchaseRequestListId: string }>(),
        'Success': props<{ purchaseRequestList: PurchaseRequestList }>(),
        'Failure': props<{ error: any }>()
    }
})

export const LoadPurchaseRequestListsByIds = createActionGroup({
    source: 'Common LoadPurchaseRequestListsByIds',
    events: {
        'Initial': props<{ purchaseRequestListIds: string[] }>(),
        'Success': props<{ purchaseRequestLists: PurchaseRequestList[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const LoadPurchaseRequestListsWithFollowerId = createActionGroup({
    source: 'Common LoadPurchaseRequestListsWithFollowerId',
    events: {
        'Initial': props<{ userId: string }>(),
        'Success': props<{ purchaseRequestLists: PurchaseRequestList[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const SetPurchaseRequest = createActionGroup({
    source: 'Common SetPurchaseRequest',
    events: {
        'Initial': props<{ purchaseRequest: PurchaseRequest }>(),
        'Success': props<{ purchaseRequest: PurchaseRequest }>(),
        'Failure': props<{ error: any }>()
    }
})

/* Files only. Attaching a file used to go through SetPurchaseRequest, which writes the WHOLE
   document from the copy the dialog opened with — so uploading a file could revert a quantity, a
   price or a plan someone changed while the file picker was open. The files array is the only
   thing this dialog owns. */
export const UpdatePurchaseRequestFiles = createActionGroup({
    source: 'Common UpdatePurchaseRequestFiles',
    events: {
        'Initial': props<{ requestId: string, files: UploadedFile[] }>(),
        'Success': props<{ requestId: string, files: UploadedFile[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const SetPurchaseRequests = createActionGroup({
    source: 'Common SetPurchaseRequests',
    events: {
        'Initial': props<{ purchaseRequests: PurchaseRequest[] }>(),
        'Success': props<{ purchaseRequests: PurchaseRequest[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const LoadPurchaseRequests = createActionGroup({
    source: 'Common LoadPurchaseRequests',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ purchaseRequests: PurchaseRequest[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const LoadPurchaseRequestsByPurchaseRequestListId = createActionGroup({
    source: 'Common LoadPurchaseRequestsByPurchaseRequestListId',
    events: {
        'Initial': props<{ purchaseRequestListId: string }>(),
        'Success': props<{ purchaseRequests: PurchaseRequest[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const LoadPurchaseRequestById = createActionGroup({
    source: 'Common LoadPurchaseRequestById',
    events: {
        'Initial': props<{ purchaseRequestId: string }>(),
        'Success': props<{ purchaseRequest: PurchaseRequest }>(),
        'Failure': props<{ error: any }>()
    }
})

export const LoadIncompletePurchaseRequests = createActionGroup({
    source: 'Common LoadIncompletePurchaseRequests',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ purchaseRequests: PurchaseRequest[] }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadOrderedPurchaseRequestsByHardwareId = createActionGroup({
    source: 'Common LoadOrderedPurchaseRequestsByHardwareItemId',
    events: {
        'Initial': props<{ hardwareItemId: string }>(),
        'Success': props<{ purchaseRequests: PurchaseRequest[] }>(),
        'Failure': props<{ error: any }>()
    }
})

export const LoadContacts = createActionGroup({
    source: 'Common LoadContacts',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ contacts: Contact[] }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const SetContact = createActionGroup({
    source: 'Common SetContact',
    events: {
        'Initial': props<{ contact: Contact }>(),
        'Success': props<{ contact: Contact }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadContactById = createActionGroup({
    source: 'Common LoadContactById',
    events: {
        'Initial': props<{ contactId: string }>(),
        'Success': props<{ contact: Contact }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadContactAssociationByCustomerId = createActionGroup({
    source: 'Common LoadContactAssociationByCustomerId',
    events: {
        'Initial': props<{ customerId: string }>(),
        'Success': props<{ contacts: Contact[] }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadSoftwareRequests = createActionGroup({
    source: 'Common LoadSoftwareRequests',
    events: {
        'Initial': emptyProps(),
        'Success': props<{ requests: SoftwareRequest[] }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const LoadSoftwareRequestById = createActionGroup({
    source: 'Common LoadSoftwareRequestById',
    events: {
        'Initial': props<{ requestId: string }>(),
        'Success': props<{ request: SoftwareRequest }>(),
        'Failure': props<{ error: any }>(),
    }
})

export const SetSoftwareRequest = createActionGroup({
    source: 'Common SetSoftwareRequest',
    events: {
        'Initial': props<{ request: SoftwareRequest }>(),
        'Success': props<{ request: SoftwareRequest }>(),
        'Failure': props<{ error: any }>(),
    }
})